import { ProjectAssignment } from '../models/ProjectAssignment';
import { CodeSnippetGroup, CodeSnippet } from '../models';
import {
    AlphaSettingsModel,
    CodeSnippetType,
    CustomRole,
    ProjectDocumentCountModel,
    CreateUpdateUserProjectAssignmentsModel,
    CreateUpdateGroupProjectAssignmentsModel,
    CreateUpdateProjectUserAssignmentsModel,
    LoadedMlModel,
    TargetMlModelService
} from '../types';
import { BulkUserBatch, BulkUserImportRequest, BulkUserRow, BulkUserValidationResult } from '../types/BulkUserModels';
import { ProcessType } from '../../../modules/common/types';
import { UsersGroup } from '../types/GroupModel';
import { UserModel } from '../types/UserModel';
import { RoleModel } from '../types/RoleModel';
import appClient from '../../common/services/AppClient';
import { ResponseData } from '../../common/services/AppClient';
import { AlphaConfig } from '../models/AlphaConfig';
import { ResultApi } from '../../common/services/AppClient';
import { endpoints } from '../../../config';
import { GroupRoleMapping } from '../types/GroupRoleMapping';
import type { ApiKey, ApiKeyInput, ApiKeyScope, ApiKeySecretResponse, CreateApiKeyInput } from '../types/ApiKey';

enum PackageStatus {
    All = 'All',
    Read = 'Ready',
    Importing = 'Importing',
    Broken = 'Broken'
}

export type Group = {
    users: UserModel[];
    usersGroup: UsersGroup;
};

export class GlobalAdministrationService {
    async processByStatus(packageStatus: string, processType: ProcessType) {
        const url =
            processType === ProcessType.Reparse
                ? endpoints.manageUrl + 'admin/packages/parse'
                : endpoints.manageUrl + 'admin/packages/reindex';
        let data: Partial<{ options: string; value: string }>;

        if (packageStatus === PackageStatus.All) {
            data = { options: 'All' };
        } else {
            data = { options: 'ByState', value: packageStatus };
        }

        const resp = await appClient.post(url, data);
        resp.mapErr(() => {
            console.error(`Error occured while reindexing packages with status ${packageStatus}`);
        });
    }

    async getRoles(): Promise<RoleModel[]> {
        let url = endpoints.usersUrl + 'roles';
        const resp = await appClient.get<RoleModel[]>(url);

        return resp.unwrapOr(Array<RoleModel>());
    }

    async getUserCount(searchKey?: string): Promise<number> {
        let url = endpoints.usersUrl + 'count';
        if (searchKey) {
            url += `?searchKey=${searchKey}`;
        }
        const resp = await appClient.get<number>(url);

        return resp.unwrapOr(0);
    }

    async getGroups(): Promise<Group[]> {
        let url = `${endpoints.usersUrl}groups`;
        const resp = await appClient.get<Group[]>(url);

        return resp.unwrapOr(Array<Group>());
    }

    async getGroupsRoleMappings(): Promise<GroupRoleMapping[]> {
        let url = `${endpoints.usersUrl}groups-role-mappings`;
        const resp = await appClient.get<GroupRoleMapping[]>(url);

        return resp.unwrapOr(Array<GroupRoleMapping>());
    }

    async getAppUsers(includeRoles: boolean = false): Promise<UserModel[]> {
        let url = `${endpoints.usersUrl}?includeRoles=${includeRoles}`;
        const resp = await appClient.get<UserModel[]>(url);
        return resp.unwrapOr(Array<UserModel>());
    }

    async getAppUsersPage(
        offset: number = 0,
        limit: number = 100,
        searchKey: string | null = null,
        roleId: string | null = null
    ): Promise<UserModel[]> {
        const request = {
            offset,
            limit,
            searchKey,
            roleId
        };
        let url = `${endpoints.usersUrl}user-pages`;
        const resp = await appClient.post<UserModel[]>(url, request);
        return resp.unwrapOr(Array<UserModel>());
    }

    async getProjectAssignments(): Promise<ProjectAssignment[] | null> {
        const url = `${endpoints.manageUrl}admin/projects/assignments`;
        const result = await appClient.get<ProjectAssignment[]>(url);
        return result.match(
            assignments => assignments,
            () => null
        );
    }

    async getCodeSnippetGroups(): Promise<CodeSnippetGroup[] | null> {
        const url = `${endpoints.manageUrl}code_snippet_groups`;
        const result = await appClient.get<CodeSnippetGroup[]>(url);
        return result.match(
            value => value,
            () => null
        );
    }

    async getCodeSnippets(): Promise<CodeSnippet[] | null> {
        const url = `${endpoints.manageUrl}code_snippets`;
        const result = await appClient.get<CodeSnippet[]>(url);
        return result.match(
            value => value,
            () => null
        );
    }

    async getCodeSnippetsByType(type: CodeSnippetType): Promise<CodeSnippet[] | null> {
        const url = `${endpoints.manageUrl}code_snippets?type=${encodeURIComponent(type)}`;
        const result = await appClient.get<CodeSnippet[]>(url);
        return result.match(
            value => value,
            () => null
        );
    }

    async getAlphaConfigs(): Promise<AlphaConfig[]> {
        const url = `${endpoints.manageUrl}alpha-configs`;
        const result = await appClient.get<AlphaConfig[]>(url);
        return result.unwrapOr([]);
    }

    getApiKeys(): Promise<ResultApi<ApiKey[]>> {
        return appClient.get<ApiKey[]>(`${endpoints.manageUrl}api-keys`);
    }

    getApiKeyScopes(): Promise<ResultApi<ApiKeyScope[]>> {
        return appClient.get<ApiKeyScope[]>(`${endpoints.manageUrl}api-keys/scopes`);
    }

    createApiKey(input: CreateApiKeyInput): Promise<ResultApi<ApiKeySecretResponse>> {
        return appClient.post<ApiKeySecretResponse>(`${endpoints.manageUrl}api-keys`, input);
    }

    updateApiKey(id: string, input: ApiKeyInput): Promise<ResultApi<ApiKey>> {
        return appClient.update<ApiKey>(`${endpoints.manageUrl}api-keys/${encodeURIComponent(id)}`, input);
    }

    setApiKeyEnabled(id: string, enabled: boolean): Promise<ResultApi<ApiKey>> {
        const action = enabled ? 'enable' : 'disable';
        return appClient.post<ApiKey>(`${endpoints.manageUrl}api-keys/${encodeURIComponent(id)}/${action}`);
    }

    regenerateApiKey(id: string): Promise<ResultApi<ApiKeySecretResponse>> {
        return appClient.post<ApiKeySecretResponse>(
            `${endpoints.manageUrl}api-keys/${encodeURIComponent(id)}/regenerate`
        );
    }

    revokeApiKey(id: string): Promise<ResultApi<ApiKey>> {
        return appClient.post<ApiKey>(`${endpoints.manageUrl}api-keys/${encodeURIComponent(id)}/revoke`);
    }

    async createUpdateGroupProjectAssignments(model: CreateUpdateGroupProjectAssignmentsModel) {
        const url = endpoints.manageUrl + 'admin/projects/group-project-assignments';
        await appClient.post(url, model);
    }

    async synchronizeAssignmentsWithKeycloak() {
        const url = endpoints.manageUrl + 'admin/projects/user-project-assignments/synchronize';
        return appClient.post(url);
    }

    async createUpdateUserProjectAssignments(model: CreateUpdateUserProjectAssignmentsModel) {
        const url = endpoints.manageUrl + 'admin/projects/user-project-assignments';
        await appClient.post(url, model);
    }

    async createUpdateProjectUserAssignments(model: CreateUpdateProjectUserAssignmentsModel) {
        const url = endpoints.manageUrl + 'admin/projects/project-user-assignments';
        await appClient.post(url, model);
    }

    async saveCodeSnippetGroup(group: CodeSnippetGroup) {
        const url = endpoints.manageUrl + 'admin/projects/snippet-group';
        await appClient.post(url, group);
    }

    async deleteCodeSnippetGroup(groupId: string) {
        const url = endpoints.manageUrl + `admin/projects/snippet-group/${groupId}`;
        await appClient.delete(url);
    }

    async saveCodeSnippet(snippet: CodeSnippet) {
        const url = endpoints.manageUrl + 'admin/projects/code-snippet';
        await appClient.post(url, snippet);
    }

    async deleteCodeSnippet(snippetId: string) {
        const url = endpoints.manageUrl + `admin/projects/code-snippet/${snippetId}`;
        await appClient.delete(url);
    }

    async createUpdateUser(userData: UserModel): Promise<ResponseData> {
        const url = `${endpoints.usersUrl}`;
        return appClient.postRaw(url, userData);
    }

    async downloadBulkUsersTemplate(): Promise<Blob | undefined> {
        const url = `${endpoints.usersUrl}bulk-users/template`;
        const resp = await appClient.get<Blob>(url, 'blob');
        return resp.isOk() ? resp.value : undefined;
    }

    async validateBulkUsersFile(file: File): Promise<ResponseData> {
        const formData = new FormData();
        formData.append('file', file);
        const url = `${endpoints.usersUrl}bulk-users/validate`;
        return appClient.postFormRaw(url, formData);
    }

    async validateBulkUsersRows(rows: BulkUserRow[]): Promise<BulkUserValidationResult> {
        const url = `${endpoints.usersUrl}bulk-users/validate-rows`;
        const resp = await appClient.post<BulkUserValidationResult>(url, rows);
        return resp.unwrapOr({ rows, errors: ['Could not validate user rows. Please try again.'], hasErrors: true });
    }

    async importBulkUsers(request: BulkUserImportRequest): Promise<ResponseData> {
        const url = `${endpoints.usersUrl}bulk-users/import`;
        return appClient.postRaw(url, request);
    }

    async getBulkUserBatches(): Promise<BulkUserBatch[]> {
        const url = `${endpoints.usersUrl}bulk-users/batches`;
        const resp = await appClient.get<BulkUserBatch[]>(url);
        return resp.unwrapOr(Array<BulkUserBatch>());
    }

    async exportBulkUsers(mode: 'all' | 'batch', batchId?: string): Promise<Blob | undefined> {
        const batchParam = batchId ? `&batchId=${encodeURIComponent(batchId)}` : '';
        const url = `${endpoints.usersUrl}bulk-users/export?mode=${mode}${batchParam}`;
        const resp = await appClient.get<Blob>(url, 'blob');
        return resp.isOk() ? resp.value : undefined;
    }

    async createUpdateGroup(name: string, id: string | null): Promise<ResponseData> {
        const url = `${endpoints.usersUrl}group`;
        const data = { name: name, id: id };
        return appClient.postRaw(url, data);
    }

    async deleteGroup(id: string): Promise<boolean> {
        const url = `${endpoints.usersUrl}group/${id}`;
        const resp = await appClient.delete<boolean>(url);
        return resp.unwrapOr(false);
    }

    async updateDomainUser(userId: string, roles: string, groupId: string): Promise<ResponseData> {
        const url = `${endpoints.usersUrl}domainUser`;
        const data = { groupId, userId, roles };
        return appClient.postRaw(url, data);
    }

    async changeUserGroup(
        userId: string,
        currentGroupId: string | undefined,
        newGroup: UsersGroup | undefined
    ): Promise<ResponseData> {
        const url = `${endpoints.usersUrl}group/change`;
        const data = { userId: userId, currentGroupId: currentGroupId, newGroup: newGroup };
        return appClient.postRaw(url, data);
    }

    async deleteUser(id: string, isDomainUser: boolean): Promise<ResponseData> {
        const url = `${endpoints.usersUrl}${id}/${isDomainUser}`;
        return appClient.deleteRaw(url);
    }

    async resetPassword(password: string, userId?: string): Promise<ResponseData> {
        const data = { password: password, userId: userId };
        const url = endpoints.usersUrl + 'userProfile/password';
        return appClient.postRaw(url, data);
    }

    async changePassword(newPassword: string, oldPassword: string): Promise<ResponseData> {
        const data = { oldPassword, newPassword };
        const url = endpoints.usersUrl + 'userProfile/password_change';
        return appClient.postRaw(url, data);
    }

    async changeObjectRoleAssignments(
        userId: string,
        objectId: string,
        objectType: string,
        roles: string[]
    ): Promise<ResponseData> {
        const data = { userId, objectId, objectType, roles };
        const url = endpoints.usersUrl + 'object-role-assignments/change';
        return appClient.postRaw(url, data);
    }

    async getCustomRoles(): Promise<CustomRole[]> {
        const url = endpoints.manageUrl + 'customRolesManagement';
        var resp = await appClient.get<CustomRole[]>(url);
        return resp.unwrapOr(Array<CustomRole>());
    }

    async createCustomRole(name: string, permissions: string[]) {
        const data = { name, permissions };
        const url = endpoints.manageUrl + 'customRolesManagement';
        return appClient.postRaw(url, data);
    }

    async updateCustomRole(id: string, name: string, permissions: string[]) {
        const data = { id, name, permissions };
        const url = endpoints.manageUrl + 'customRolesManagement';
        return appClient.updateRaw(url, data);
    }

    async deleteCustomRole(id: string) {
        const url = endpoints.manageUrl + `customRolesManagement/${id}`;
        return appClient.deleteRaw(url);
    }

    async updateAlphaConfigValue(data: {}) {
        let url = `${endpoints.manageUrl}alpha-configs`;
        return appClient.post(url, { data });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async getAlphaSettings(): Promise<AlphaSettingsModel | undefined> {
        const url = `${endpoints.manageUrl}alpha-settings`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = await appClient.get<AlphaSettingsModel | undefined>(url);
        return result.unwrapOr(undefined);
    }

    async updateAlphaSettingsValue(key: string, value: unknown): Promise<ResultApi<string | undefined>> {
        const data = { key, value };
        const url = `${endpoints.manageUrl}alpha-settings`;
        return appClient.post(url, data);
    }

    async deleteAlphaSettingsValue(key: string) {
        const url = `${endpoints.manageUrl}alpha-settings/${key}`;
        return appClient.delete(url);
    }

    async getProjectDocumentsCount() {
        const url = `${endpoints.manageUrl}system/projects/info`;
        return appClient.get<ProjectDocumentCountModel[]>(url);
    }

    async getJobsCount() {
        const url = `${endpoints.manageUrl}system/jobs/info`;
        return appClient.get<number>(url);
    }

    async clearQueue(): Promise<ResultApi<number>> {
        const url = `${endpoints.manageUrl}admin/queue/clear`;
        return appClient.post(url);
    }

    // Get currently loaded ML models from all services
    async getLoadedModels(): Promise<ResultApi<LoadedMlModel[]>> {
        const url = endpoints.manageUrl + 'admin/models/loaded';
        return appClient.get<LoadedMlModel[]>(url);
    }

    // Unload a specific ML model from the specified service
    async unloadMlModel(modelId: string, targetService: TargetMlModelService): Promise<ResultApi<void>> {
        const url = endpoints.manageUrl + 'admin/models/unload';
        const data = { modelId, targetService };
        return appClient.post(url, data);
    }
}
